import { Button, Divider, IconButton, List, ListItem, ListItemText, Stack, TextField, Typography } from '@mui/material'
import { Box } from '@mui/system'
import React, { useState, useEffect, useCallback } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'
import type { AuthWebAuthnCredential } from 'aws-amplify/auth'
import { useAuth } from 'src/common/context'
import * as S from './styles'
import { Modal, alertNotification } from 'src/ui'
import SetupMfa from '../Mfa/SetupMfa'
import { Visibility, VisibilityOff, Delete, Edit } from '@mui/icons-material'
import { calculatePasswordStrength } from 'src/common/helpers'
import { getPasskeyLabels, notifyPasskeyEvent } from 'src/common/api/accountSecurity'
import { passkeyErrorMessage } from 'src/common/context/auth/passkeyErrors'

// Destructive / enrollment passkey actions require a recently authenticated session (R9.2, R14). We treat a
// session as fresh if the last authentication was within this window; otherwise the user is asked to log in
// again before managing passkeys.
const FRESH_SESSION_SECONDS = 5 * 60

interface PasskeyRow {
  credentialId: string
  label: string
  createdAt?: Date
}

const Security: React.FC = () => {
  const { user, getPreferredMfa, changePassword, isPasskeySupported, registerPasskey, listPasskeys, deletePasskey } =
    useAuth()
  const [mfaType, setMfaType] = useState('')
  const isMfaEnabled = mfaType === 'SOFTWARE_TOKEN_MFA'
  const passkeySupported = isPasskeySupported()
  const setupMfaParams = {
    email: '',
    secret: ''
  }
  const [modals, setModals] = useState({
    setupMfa: false,
    changePw: false,
    addPasskey: false,
    renamePasskey: false,
    removePasskey: false
  })
  const [loading, setLoading] = useState(false)
  const [passwords, setPasswords] = useState({
    oldPassword: '',
    newPassword: ''
  })
  const [visible, setVisible] = useState(false)

  // Passkey state
  const [passkeys, setPasskeys] = useState<PasskeyRow[]>([])
  const [passkeysLoading, setPasskeysLoading] = useState(false)
  const [passkeyBusy, setPasskeyBusy] = useState(false)
  const [labelInput, setLabelInput] = useState('')
  const [activePasskey, setActivePasskey] = useState<PasskeyRow | null>(null)

  const handleOpen = (modal: string) => {
    setModals((s) => ({ ...s, [modal]: true }))
  }

  const handleClose = (modal: string) => {
    setModals((s) => ({ ...s, [modal]: false }))
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPasswords({
      ...passwords,
      [event.target.name]: event.target.value
    })
  }

  const handleRequest = async () => {
    const { error } = calculatePasswordStrength(passwords.newPassword)
    if (error) {
      alertNotification(error)
      return
    }
    try {
      setLoading(true)
      await changePassword(passwords.oldPassword, passwords.newPassword)
      setPasswords({
        oldPassword: '',
        newPassword: ''
      })
      alertNotification('Password changed successfully!', 'success')
    } catch (e) {
      alertNotification((e as Error).message)
    }
    setLoading(false)
  }

  useEffect(() => {
    const getMfaType = async (): Promise<void> => {
      try {
        if (!user) return
        const preferredMfa: string = await getPreferredMfa()
        setMfaType(preferredMfa)
      } catch (error) {
        alertNotification()
      }
    }
    getMfaType()
  }, [user])

  // --- Passkeys ---

  // Merge Cognito's credential list with our backend labels (Cognito can't store an editable name).
  const loadPasskeys = useCallback(async () => {
    if (!passkeySupported) return
    setPasskeysLoading(true)
    try {
      const [credentials, { labels }] = await Promise.all([listPasskeys(), getPasskeyLabels()])
      const labelMap = new Map(labels.map((l) => [l.credentialId, l.label]))
      const rows: PasskeyRow[] = credentials
        .filter((c): c is AuthWebAuthnCredential & { credentialId: string } => Boolean(c.credentialId))
        .map((c) => ({
          credentialId: c.credentialId,
          label: labelMap.get(c.credentialId) || c.friendlyCredentialName || 'Unnamed passkey',
          createdAt: c.createdAt
        }))
      setPasskeys(rows)
    } catch (e) {
      alertNotification('Could not load passkeys.')
    }
    setPasskeysLoading(false)
  }, [passkeySupported])

  useEffect(() => {
    if (user && passkeySupported) loadPasskeys()
  }, [user, passkeySupported, loadPasskeys])

  // Returns true if the current session was authenticated recently enough to manage passkeys.
  const isSessionFresh = async (): Promise<boolean> => {
    try {
      const session = await fetchAuthSession()
      const authTime = session.tokens?.accessToken?.payload?.auth_time as number | undefined
      if (!authTime) return false
      return Date.now() / 1000 - authTime <= FRESH_SESSION_SECONDS
    } catch {
      return false
    }
  }

  const requireFreshSession = async (): Promise<boolean> => {
    const fresh = await isSessionFresh()
    if (!fresh) {
      alertNotification('For your security, please log out and sign in again before managing passkeys.')
    }
    return fresh
  }

  // Add: run the WebAuthn ceremony, resolve the newly created credentialId by diffing the list, then collect
  // a label and persist it via the backend (which also emails + audits).
  const handleAddPasskey = async () => {
    if (!(await requireFreshSession())) return
    setPasskeyBusy(true)
    try {
      const before = new Set(passkeys.map((p) => p.credentialId))
      await registerPasskey()
      const credentials = await listPasskeys()
      const created = credentials.find((c) => c.credentialId && !before.has(c.credentialId))
      if (!created?.credentialId) {
        // Registered but couldn't resolve which one — refresh and let the user rename it.
        await loadPasskeys()
        alertNotification('Passkey added.', 'success')
        return
      }
      setActivePasskey({ credentialId: created.credentialId, label: created.friendlyCredentialName || '' })
      setLabelInput(created.friendlyCredentialName || 'My passkey')
      handleOpen('addPasskey')
    } catch (e) {
      // Friendly copy for cancel/timeout, duplicate authenticator (R11 edge), credential limit (R11), and
      // passkeys-not-configured.
      alertNotification(passkeyErrorMessage(e, 'register'))
    } finally {
      setPasskeyBusy(false)
    }
  }

  const handleConfirmAddLabel = async () => {
    if (!activePasskey) return
    setPasskeyBusy(true)
    try {
      await notifyPasskeyEvent({ event: 'added', credentialId: activePasskey.credentialId, label: labelInput.trim() })
      handleClose('addPasskey')
      setActivePasskey(null)
      await loadPasskeys()
      alertNotification('Passkey added.', 'success')
    } catch (e) {
      alertNotification((e as Error).message)
    }
    setPasskeyBusy(false)
  }

  const handleConfirmRename = async () => {
    if (!activePasskey) return
    setPasskeyBusy(true)
    try {
      await notifyPasskeyEvent({ event: 'renamed', credentialId: activePasskey.credentialId, label: labelInput.trim() })
      handleClose('renamePasskey')
      setActivePasskey(null)
      await loadPasskeys()
      alertNotification('Passkey renamed.', 'success')
    } catch (e) {
      alertNotification((e as Error).message)
    }
    setPasskeyBusy(false)
  }

  const handleConfirmRemove = async () => {
    if (!activePasskey) return
    if (!(await requireFreshSession())) return
    setPasskeyBusy(true)
    try {
      await deletePasskey(activePasskey.credentialId)
      await notifyPasskeyEvent({ event: 'removed', credentialId: activePasskey.credentialId })
      handleClose('removePasskey')
      setActivePasskey(null)
      await loadPasskeys()
      alertNotification('Passkey removed.', 'success')
    } catch (e) {
      alertNotification((e as Error).message)
    }
    setPasskeyBusy(false)
  }

  return (
    <>
      <S.Flex>
        <Box>
          <Typography fontWeight={500}>Two factor authentication</Typography>
        </Box>
        {isMfaEnabled ? (
          <S.TierChip variant="outlined" color="success" label="2-FA enabled" />
        ) : (
          <S.TierChipError variant="outlined" color="error" label="2-FA not set" />
        )}
      </S.Flex>
      <Divider />
      <S.Flex>
        <Box>
          <Typography fontWeight={500}>Password</Typography>
          <Typography color="grey.600">
            Remember not to store your password in your email or cloud and don&apos;t share it with anyone.
          </Typography>
        </Box>
        <Button size="small" onClick={() => handleOpen('changePw')}>
          Change password
        </Button>
      </S.Flex>
      <Divider />
      {passkeySupported && (
        <>
          <S.Flex>
            <Box>
              <Typography fontWeight={500}>Passkeys</Typography>
              <Typography color="grey.600">
                A passkey lets you sign in with your fingerprint, face, or device PIN instead of a password.
              </Typography>
            </Box>
            <Button size="small" onClick={handleAddPasskey} loading={passkeyBusy}>
              Add a passkey
            </Button>
          </S.Flex>
          {passkeysLoading ? (
            <Typography color="grey.600" sx={{ pb: 2 }}>
              Loading passkeys…
            </Typography>
          ) : passkeys.length === 0 ? (
            <Typography color="grey.600" sx={{ pb: 2 }}>
              You don&apos;t have any passkeys yet.
            </Typography>
          ) : (
            <List dense>
              {passkeys.map((pk) => (
                <ListItem
                  key={pk.credentialId}
                  secondaryAction={
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        edge="end"
                        aria-label="rename"
                        onClick={() => {
                          setActivePasskey(pk)
                          setLabelInput(pk.label)
                          handleOpen('renamePasskey')
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="remove"
                        onClick={() => {
                          setActivePasskey(pk)
                          handleOpen('removePasskey')
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Stack>
                  }
                >
                  <ListItemText
                    primary={pk.label}
                    secondary={pk.createdAt ? `Added ${new Date(pk.createdAt).toLocaleDateString()}` : undefined}
                  />
                </ListItem>
              ))}
            </List>
          )}
          <Divider />
        </>
      )}
      {modals.setupMfa && (
        <SetupMfa
          open={modals.setupMfa}
          setupMfaParams={setupMfaParams}
          onClose={() => handleClose('setupMfa')}
          enforced
        />
      )}
      <Modal
        open={modals.changePw}
        onClose={() => handleClose('changePw')}
        title="Change Password"
        maxWidth="xs"
        fullWidth
      >
        <Stack spacing={3}>
          <Box>
            <Typography mb={1}>Current Password</Typography>
            <TextField
              value={passwords.oldPassword}
              onChange={handleChange}
              size="small"
              fullWidth
              name="oldPassword"
              placeholder="Current Password"
              type={visible ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <S.StyledInputAdornment position="end">
                    <IconButton onClick={() => setVisible(!visible)}>
                      {visible ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </S.StyledInputAdornment>
                )
              }}
            />
          </Box>
          <Box>
            <Typography mb={1}>New Password</Typography>
            <TextField
              value={passwords.newPassword}
              onChange={handleChange}
              size="small"
              fullWidth
              name="newPassword"
              placeholder="New Password"
              type={visible ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <S.StyledInputAdornment position="end">
                    <IconButton onClick={() => setVisible(!visible)}>
                      {visible ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </S.StyledInputAdornment>
                )
              }}
            />
          </Box>
          <Typography variant="subtitle2">
            Password should contain at least 1 lowercase, 1 uppercase, 1 number and 1 special character, with a minimum
            length of 8 characters.
          </Typography>
          <Button
            variant="contained"
            size="large"
            loading={loading}
            fullWidth
            onClick={handleRequest}
            disabled={!passwords.oldPassword || !passwords.newPassword}
          >
            Change Password
          </Button>
        </Stack>
      </Modal>

      <Modal
        open={modals.addPasskey}
        onClose={() => handleClose('addPasskey')}
        title="Name your passkey"
        maxWidth="xs"
        fullWidth
      >
        <Stack spacing={3}>
          <Typography>Give this passkey a name so you can recognise it later.</Typography>
          <TextField
            value={labelInput}
            onChange={(e) => setLabelInput(e.target.value)}
            size="small"
            fullWidth
            placeholder="e.g. MacBook Pro"
            inputProps={{ maxLength: 120 }}
          />
          <Button
            variant="contained"
            size="large"
            fullWidth
            loading={passkeyBusy}
            disabled={!labelInput.trim()}
            onClick={handleConfirmAddLabel}
          >
            Save
          </Button>
        </Stack>
      </Modal>

      <Modal
        open={modals.renamePasskey}
        onClose={() => handleClose('renamePasskey')}
        title="Rename passkey"
        maxWidth="xs"
        fullWidth
      >
        <Stack spacing={3}>
          <TextField
            value={labelInput}
            onChange={(e) => setLabelInput(e.target.value)}
            size="small"
            fullWidth
            placeholder="Passkey name"
            inputProps={{ maxLength: 120 }}
          />
          <Button
            variant="contained"
            size="large"
            fullWidth
            loading={passkeyBusy}
            disabled={!labelInput.trim()}
            onClick={handleConfirmRename}
          >
            Save
          </Button>
        </Stack>
      </Modal>

      <Modal
        open={modals.removePasskey}
        onClose={() => handleClose('removePasskey')}
        title="Remove passkey"
        maxWidth="xs"
        fullWidth
      >
        <Stack spacing={3}>
          <Typography>
            Remove <strong>{activePasskey?.label}</strong>? You won&apos;t be able to sign in with it anymore. You can
            always sign in with your password.
          </Typography>
          <Button
            variant="contained"
            color="error"
            size="large"
            fullWidth
            loading={passkeyBusy}
            onClick={handleConfirmRemove}
          >
            Remove passkey
          </Button>
        </Stack>
      </Modal>
    </>
  )
}

export default Security
